// src/components/ConfirmationPopup.jsx
import React from 'react';
import { FiAlertCircle, FiCheckCircle, FiX } from 'react-icons/fi';

const ConfirmationPopup = ({
    isOpen,
    title,
    message,
    confirmText = "Confirm",
    cancelText = "Cancel",
    onConfirm,
    onCancel,
    type = "warning", // "warning", "danger", "success", "info"
    loading = false
}) => {
    if (!isOpen) return null;

    const getTypeStyles = () => {
        switch (type) {
            case "danger":
                return {
                    icon: <FiAlertCircle className="w-6 h-6" />,
                    iconBg: "bg-red-100",
                    iconColor: "text-red-600",
                    confirmBg: "bg-red-600 hover:bg-red-700 focus:ring-red-500",
                    confirmText: "text-white"
                };
            case "success":
                return {
                    icon: <FiCheckCircle className="w-6 h-6" />,
                    iconBg: "bg-green-100",
                    iconColor: "text-green-600",
                    confirmBg: "bg-green-600 hover:bg-green-700 focus:ring-green-500",
                    confirmText: "text-white"
                };
            case "info":
                return {
                    icon: <FiAlertCircle className="w-6 h-6" />,
                    iconBg: "bg-blue-100",
                    iconColor: "text-blue-600",
                    confirmBg: "bg-blue-600 hover:bg-blue-700 focus:ring-blue-500",
                    confirmText: "text-white"
                };
            default: // warning
                return {
                    icon: <FiAlertCircle className="w-6 h-6" />,
                    iconBg: "bg-amber-100",
                    iconColor: "text-amber-600",
                    confirmBg: "bg-amber-600 hover:bg-amber-700 focus:ring-amber-500",
                    confirmText: "text-white"
                };
        }
    };

    const styles = getTypeStyles();

    return (
        <div className="fixed inset-0 z-[9999] overflow-y-auto">
            {/* Backdrop */}
            <div
                className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
                onClick={onCancel}
            />

            {/* Modal Container */}
            <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
                <div className="relative transform overflow-hidden rounded-2xl bg-white shadow-2xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
                    {/* Close Button */}
                    <button
                        onClick={onCancel}
                        className="absolute right-4 top-4 p-1 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                    >
                        <FiX className="w-5 h-5" />
                    </button>

                    <div className="bg-white px-6 pb-6 pt-8 sm:px-8 sm:pb-8">
                        {/* Icon */}
                        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full mb-4">
                            <div className={`flex items-center justify-center w-full h-full rounded-full ${styles.iconBg}`}>
                                <div className={styles.iconColor}>
                                    {styles.icon}
                                </div>
                            </div>
                        </div>

                        {/* Title */}
                        <h3 className="text-xl font-bold text-gray-900 mb-3">
                            {title}
                        </h3>

                        {/* Message */}
                        <div className="mt-2">
                            <p className="text-sm text-gray-600 leading-relaxed">
                                {message}
                            </p>
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="bg-gray-50 px-6 py-4 sm:px-8 sm:flex sm:flex-row-reverse gap-3">
                        <button
                            type="button"
                            disabled={loading}
                            onClick={onConfirm}
                            className={`inline-flex w-full justify-center items-center rounded-lg px-4 py-2.5 text-sm font-semibold shadow-sm sm:w-auto min-w-[120px] transition-all ${styles.confirmBg} ${styles.confirmText} focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed`}
                        >
                            {loading ? (
                                <>
                                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Processing...
                                </>
                            ) : (
                                confirmText
                            )}
                        </button>
                        <button
                            type="button"
                            disabled={loading}
                            onClick={onCancel}
                            className="mt-3 inline-flex w-full justify-center rounded-lg bg-white px-4 py-2.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto min-w-[120px] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {cancelText}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ConfirmationPopup;